/**
 * @fileoverview useRoomDoubleClick Hook - Distinguish single vs double-click on rooms
 * @author MCHD Room Scheduler Team
 * @created 09-10-2025 04:30:44
 * 
 * Custom hook for handling single-click vs double-click on floor plan rooms.
 * Uses 300ms window to detect double-click per standard UX patterns.
 * 
 * Per Floor Plan Spec Section 3:
 * - Single click: Select room + open sidebar
 * - Double click: Open Assign modal with room prefilled
 * 
 * Implementation uses ref-based click counting with timeout to avoid
 * triggering single-click callback when double-click occurs.
 */

import { useRef, useCallback } from 'react';

// =============================================================================
// TYPES
// =============================================================================

export interface UseRoomDoubleClickOptions<T> {
  /** Callback for single click (300ms delay) */
  onSingleClick: (item: T) => void;
  
  /** Callback for double click (immediate) */
  onDoubleClick: (item: T) => void;
  
  /** Double-click detection window in ms */
  delay?: number;
}

export interface UseRoomDoubleClickReturn<T> {
  /** Handler to attach to onClick event */
  handleClick: (item: T) => void;
  
  /** Reset click state manually if needed */
  reset: () => void;
}

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

/**
 * useRoomDoubleClick - Custom hook for single/double-click detection
 * 
 * Provides clean single/double-click handling with configurable delay.
 * Prevents single-click callback from firing when double-click occurs.
 * 
 * @param options - Configuration object
 * @param options.onSingleClick - Single click callback
 * @param options.onDoubleClick - Double click callback
 * @param options.delay - Detection window (default: 300ms)
 * @returns handleClick function and reset utility
 * 
 * @example
 * ```tsx
 * const { handleClick } = useRoomDoubleClick({
 *   onSingleClick: (room) => console.log('Single:', room.number),
 *   onDoubleClick: (room) => console.log('Double:', room.number)
 * });
 * 
 * <RoomComponent onClick={() => handleClick(room)} />
 * ```
 */
export function useRoomDoubleClick<T>({
  onSingleClick,
  onDoubleClick,
  delay = 300
}: UseRoomDoubleClickOptions<T>): UseRoomDoubleClickReturn<T> {
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const clickCountRef = useRef(0);
  const lastItemRef = useRef<T | null>(null);
  
  /**
   * Handle click event
   * Logic:
   * - First click: Start 300ms timer for single-click
   * - Second click within 300ms: Cancel timer, trigger double-click
   * - Timer expires: Trigger single-click
   */
  const handleClick = useCallback((item: T) => {
    clickCountRef.current += 1;
    lastItemRef.current = item;
    
    if (clickCountRef.current === 1) {
      // First click - start timer
      clickTimeoutRef.current = setTimeout(() => {
        // Timer expired - trigger single click
        if (lastItemRef.current) {
          onSingleClick(lastItemRef.current);
        }
        clickCountRef.current = 0;
        lastItemRef.current = null;
      }, delay);
      
    } else if (clickCountRef.current === 2) {
      // Second click within window - trigger double click
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
      }
      
      if (lastItemRef.current) {
        onDoubleClick(lastItemRef.current);
      }
      
      clickCountRef.current = 0;
      lastItemRef.current = null;
    }
  }, [onSingleClick, onDoubleClick, delay]);
  
  /**
   * Reset click state manually
   * Useful when component unmounts or context changes
   */
  const reset = useCallback(() => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    clickCountRef.current = 0;
    lastItemRef.current = null;
  }, []);
  
  return { handleClick, reset };
}

export default useRoomDoubleClick;

